import { useEffect, useState } from "react";
import { useUserpilot } from "@platform/hooks/useUserpilot";

import ConnectInfo from "@assets/images/connect_screen_info.svg?url";
import ConnectSkeleton from "@assets/images/connect_screen_skeleton.svg";
import RefreshIcon from "@assets/icons/refresh-icon.svg";

import useUserStore from "@store/useUserStore";
import useSystemStore from "@store/useSystemStore";
import usePlatformStore from "@store/usePlatformStore";
import { useGetUser } from "./hooks/useGetUser";
import { useTimeoutRef } from "@hooks/useTimeoutRef";
import { cn } from "@utils/tailwind.util";
import { MERIDIAN_EMAIL_CONNECTIONS_URL } from "@config/constants";
import { ConnectionScreenState } from "@enums/screens.enum";
import { BackgroundSection } from "@components/Sections";
import { MainLogo } from "@ui/Logos/MainLogo";
import { Paragraph } from "@ui/Content";
import { Button } from "@ui/Button";
import { H3 } from "@ui/Headings";
import { View } from "@ui/View";

const RETRY_INTERVAL_MS = 3000;
const MAX_RETRY_ATTEMPTS = 60;

export default function ConnectionScreen() {
  const { track } = useUserpilot();
  const { hasSession } = useUserStore();
  const { setIsAppActive, connectionScreenState, setConnectionScreenState } = useSystemStore();
  const { isChrome } = usePlatformStore();
  const { refetch: refetchUser } = useGetUser(hasSession);
  const [isTimeOut, setIsTimeOut] = useState(false);
  const { schedule: scheduleRetry, clear: clearRetry } = useTimeoutRef();
  const isWaiting = connectionScreenState === ConnectionScreenState.Waiting;

  const connectProvider = async () => {
    setIsAppActive(true);
    setIsTimeOut(false);
    setConnectionScreenState(ConnectionScreenState.Waiting);

    window.open(MERIDIAN_EMAIL_CONNECTIONS_URL, "_blank", "noopener,noreferrer");
    track("Click: Connect to Meridian");

    await tryToConnect();
  };

  const tryToConnect = async (retryCount: number = 0) => {
    const refetchResult = await refetchUser();

    // Read fresh value from store to avoid stale closure
    const isUserConnected = useUserStore.getState().isUserConnected;

    if (refetchResult.data && isUserConnected) {
      setIsTimeOut(false);
      setConnectionScreenState(ConnectionScreenState.Connect);

      clearRetry();
      return;
    }

    if (++retryCount >= MAX_RETRY_ATTEMPTS) {
      setIsTimeOut(true);
      setConnectionScreenState(ConnectionScreenState.Reconnect);

      clearRetry();
      return;
    }

    scheduleRetry(() => tryToConnect(retryCount), RETRY_INTERVAL_MS);
  };

  useEffect(() => {
    // On mount: reset Reconnect state (timeout) back to Connect
    if (connectionScreenState === ConnectionScreenState.Reconnect) {
      setConnectionScreenState(ConnectionScreenState.Connect);
    }
    // On mount: if Waiting state, resume polling (it was interrupted by unmount)
    if (connectionScreenState === ConnectionScreenState.Waiting) {
      void tryToConnect();
    }
  }, []);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col justify-center items-center pt-[100px] pb-[30px]">
        <MainLogo className="pb-[50px]" />

        <BackgroundSection
          className={isWaiting ? "bg-teal-500" : "bg-gray-450"}
          icon={
            <RefreshIcon
              className={cn(
                "stroke-white stroke-[1.76]",
                isWaiting ? "animate-slowpin-reverse" : ""
              )}
            />
          }
          leftImage={<ConnectSkeleton className="w-[108px] rounded-xl" />}
          rightImage={<img src={ConnectInfo} alt="" className="w-[88px]" />}
        />

        <View.Condition if={!isTimeOut}>
          <div className="flex flex-col gap-3 my-[48px] px-4">
            <H3 className="text-[#406A68] font-bold">
              Connect Meridian to enhance email and calendar intelligence
            </H3>
            <Paragraph>
              {"Keep Meridian up to date without leaving " +
                (isChrome() ? "Gmail or Google Calendar." : "Outlook.")}
            </Paragraph>
          </div>
        </View.Condition>

        <View.Condition if={isTimeOut}>
          <div className="flex flex-col gap-3 my-[48px] px-4">
            <H3 className="text-yellow-600 dark:text-yellow-600 my-[19px]">Connection timed out</H3>
            <Paragraph>Authorization timed out. Please refresh the page to try again.</Paragraph>
          </div>
        </View.Condition>

        <Button
          buttonType={"PRIMARY"}
          onClick={connectProvider}
          disabled={isWaiting}
          className="min-w-[270px]"
        >
          {connectionScreenState}
        </Button>
      </div>
    </div>
  );
}
